import mergeObjects from "../utils/Merge";
import IStorageAdapter from './IStorageAdapter.js';

export default class Store<T> {
	private readonly _listeners: Set<(() => void)>;
	private readonly _storageAdapter: IStorageAdapter<T> | null | undefined;
	private _debounceTimeout: number;
	private _state: T;
	private _newState?: T;

	constructor(state: T, storageAdapter?: IStorageAdapter<T> | null | undefined) {
		if (state == null) {
			throw new Error("Initial state cannot be null or undefined");
		}

		this._debounceTimeout = 0;
		this._state = state;
		this._storageAdapter = storageAdapter;
		this._listeners = new Set();
		this._initializeFromStorage();
	}

	getState(): T {
		return this._state;
	}

	patchState(patch: ((action: T) => void)): T {
		let newState = this._newState;
		if (newState != null) {
			patch(newState);
			return newState;
		}

		newState = this._newState = structuredClone(this._state);
		patch(newState);
		queueMicrotask(() => {
			const newState = this._newState!;
			this._newState = undefined;
			this._persistState();
			this._notify(newState);
		});

		return newState;
	}

	setState(state: T): void {
		this._notify(state);
		this._persistState();
	}

	/**
	 * Subscribe to store changes.
	 * @param listener
	 * @returns A function that removes the subscription.
	 */
	subscribe(listener: (() => void)): (() => void) {
		this._listeners.add(listener);
		return () => this._listeners.delete(listener);
	}

	private _initializeFromStorage(): void {
		if (this._storageAdapter == null) {
			return;
		}
		this._storageAdapter.load().then((loadedState) => {
			if (loadedState == null) {
				return;
			}

			this._notify(mergeObjects<T>(this._state, loadedState));
		});
	}

	private _notify(changedState: T): void {
		this._state = changedState;
		this._listeners.forEach((listener) => listener());
	}

	private _persistState(): void {
		if (this._storageAdapter == null) {
			return;
		}

		if (this._debounceTimeout > 0) {
			clearTimeout(this._debounceTimeout);
		}

		this._debounceTimeout = setTimeout(() => {
			this._storageAdapter!
				.save(this._state)
				.catch((reason) => {
					console.log(reason)
				});
		}, 250);
	}
}
